import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import {
    Users,
    AlertTriangle,
    GitMerge,
    ArrowLeft,
    RefreshCw,
    Mail,
    Phone,
    Calendar as CalendarIcon,
    ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
}

export default function ProspectsDoublons() {
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [merging, setMerging] = useState(false);
    // selections: { [groupKey]: { masterId: string, dupIds: Set<string> } }
    const [selections, setSelections] = useState({});
    const [confirmGroup, setConfirmGroup] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/admin/prospects/duplicates`, { withCredentials: true });
            setGroups(res.data.groups || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur de chargement des doublons');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const groupKey = (g) => `${g.match_type}:${g.key}`;

    const setMaster = (g, prospectId) => {
        const k = groupKey(g);
        setSelections(prev => {
            const cur = prev[k] || { masterId: '', dupIds: new Set() };
            const newDups = new Set(cur.dupIds);
            // The master cannot be in dupIds
            newDups.delete(prospectId);
            return { ...prev, [k]: { masterId: prospectId, dupIds: newDups } };
        });
    };

    const toggleDup = (g, prospectId) => {
        const k = groupKey(g);
        setSelections(prev => {
            const cur = prev[k] || { masterId: '', dupIds: new Set() };
            if (cur.masterId === prospectId) return prev; // can't toggle the master
            const newDups = new Set(cur.dupIds);
            if (newDups.has(prospectId)) newDups.delete(prospectId);
            else newDups.add(prospectId);
            return { ...prev, [k]: { ...cur, dupIds: newDups } };
        });
    };

    const openConfirm = (g) => {
        const sel = selections[groupKey(g)];
        if (!sel || !sel.masterId) {
            toast.error('Sélectionnez d\'abord le prospect maître');
            return;
        }
        if (!sel.dupIds || sel.dupIds.size === 0) {
            toast.error('Sélectionnez au moins un doublon à fusionner');
            return;
        }
        setConfirmGroup(g);
    };

    const doMerge = async () => {
        if (!confirmGroup) return;
        const sel = selections[groupKey(confirmGroup)];
        setMerging(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/prospects/merge`,
                { master_id: sel.masterId, duplicate_ids: Array.from(sel.dupIds) },
                { withCredentials: true },
            );
            toast.success(res.data.message || 'Fusion effectuée');
            setConfirmGroup(null);
            // Clear selection for that group
            setSelections(prev => {
                const next = { ...prev };
                delete next[groupKey(confirmGroup)];
                return next;
            });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Échec de la fusion');
        } finally {
            setMerging(false);
        }
    };

    const totalProspects = groups.reduce((acc, g) => acc + g.count, 0);

    return (
        <Layout>
            <div className="space-y-6 p-4 md:p-6">
                <Toaster position="top-right" />

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/administration')}
                            data-testid="doublons-back-btn"
                        >
                            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Nettoyage des doublons
                            </h1>
                            <p className="text-sm text-gray-500">
                                Détection automatique des prospects en double (email ou téléphone)
                            </p>
                        </div>
                    </div>
                    <Button onClick={load} variant="outline" disabled={loading} data-testid="doublons-refresh-btn">
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Actualiser
                    </Button>
                </div>

                {/* Summary banner */}
                <Card className="border-l-4 border-l-[#C9A962]">
                    <CardContent className="p-5 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                            <AlertTriangle className="w-6 h-6 text-amber-600" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm text-gray-500">Groupes de doublons détectés</p>
                            <p className="text-3xl font-bold text-[#1B365D]">{groups.length}</p>
                            {totalProspects > 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                    {totalProspects} prospects concernés au total
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Groups */}
                {loading ? (
                    <div className="text-center text-gray-500 py-12">Chargement...</div>
                ) : groups.length === 0 ? (
                    <Card>
                        <CardContent className="py-16 text-center">
                            <div className="inline-flex w-16 h-16 rounded-full bg-green-50 items-center justify-center mb-4">
                                <Users className="w-8 h-8 text-green-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-[#1B365D]">Aucun doublon détecté</h3>
                            <p className="text-sm text-gray-500 mt-1">Tous vos prospects sont uniques.</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {groups.map((g, idx) => {
                            const k = groupKey(g);
                            const sel = selections[k] || { masterId: '', dupIds: new Set() };
                            return (
                                <Card key={`${k}-${idx}`} data-testid="doublon-group-card">
                                    <CardHeader className="pb-3">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                            <CardTitle className="text-base font-semibold text-[#1B365D] flex items-center gap-2 flex-wrap">
                                                {g.match_type === 'email' ? (
                                                    <Mail className="w-4 h-4 text-[#C9A962]" />
                                                ) : (
                                                    <Phone className="w-4 h-4 text-[#C9A962]" />
                                                )}
                                                <Badge variant="outline" className="text-xs">
                                                    {g.match_type === 'email' ? 'Email' : 'Téléphone'}
                                                </Badge>
                                                <span className="font-mono text-sm">{g.key}</span>
                                                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                                                    {g.count} prospects
                                                </Badge>
                                            </CardTitle>
                                            <Button
                                                size="sm"
                                                onClick={() => openConfirm(g)}
                                                disabled={!sel.masterId || sel.dupIds.size === 0}
                                                className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white"
                                                data-testid="doublon-merge-btn"
                                            >
                                                <GitMerge className="w-4 h-4 mr-1" />
                                                Fusionner ({sel.dupIds.size || 0})
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50 text-gray-600">
                                                    <tr>
                                                        <th className="text-center px-2 py-2 w-16">Maître</th>
                                                        <th className="text-center px-2 py-2 w-16">Fusionner</th>
                                                        <th className="text-left px-2 py-2">Nom</th>
                                                        <th className="text-left px-2 py-2">Email</th>
                                                        <th className="text-left px-2 py-2">Téléphone</th>
                                                        <th className="text-left px-2 py-2">Statut</th>
                                                        <th className="text-left px-2 py-2">Créé le</th>
                                                        <th className="text-left px-2 py-2">Remarques</th>
                                                        <th className="text-center px-2 py-2 w-12"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {g.prospects.map((p) => {
                                                        const isMaster = sel.masterId === p.id;
                                                        const isDup = sel.dupIds.has(p.id);
                                                        return (
                                                            <tr
                                                                key={p.id}
                                                                className={`border-t ${isMaster ? 'bg-blue-50' : isDup ? 'bg-red-50' : ''}`}
                                                            >
                                                                <td className="text-center px-2 py-2">
                                                                    <input
                                                                        type="radio"
                                                                        name={`master-${k}`}
                                                                        checked={isMaster}
                                                                        onChange={() => setMaster(g, p.id)}
                                                                        data-testid="doublon-master-radio"
                                                                    />
                                                                </td>
                                                                <td className="text-center px-2 py-2">
                                                                    <Checkbox
                                                                        checked={isDup}
                                                                        disabled={isMaster}
                                                                        onCheckedChange={() => toggleDup(g, p.id)}
                                                                        data-testid="doublon-dup-checkbox"
                                                                    />
                                                                </td>
                                                                <td className="px-2 py-2 font-medium text-[#1B365D]">
                                                                    {p.nom}
                                                                    {isMaster && <Badge className="ml-2 bg-blue-100 text-blue-700 hover:bg-blue-100">Maître</Badge>}
                                                                    {isDup && <Badge className="ml-2 bg-red-100 text-red-700 hover:bg-red-100">À fusionner</Badge>}
                                                                </td>
                                                                <td className="px-2 py-2 text-gray-600">{p.email || '-'}</td>
                                                                <td className="px-2 py-2 text-gray-600">{p.telephone || '-'}</td>
                                                                <td className="px-2 py-2">
                                                                    <Badge variant="outline" className="text-xs">{p.statut || '-'}</Badge>
                                                                </td>
                                                                <td className="px-2 py-2 text-gray-500 whitespace-nowrap">
                                                                    <CalendarIcon className="w-3 h-3 inline mr-1" />
                                                                    {formatDate(p.created_at)}
                                                                </td>
                                                                <td className="px-2 py-2 text-gray-500 max-w-xs truncate" title={p.remarques || ''}>
                                                                    {p.remarques || '-'}
                                                                </td>
                                                                <td className="text-center px-2 py-2">
                                                                    <a
                                                                        href={`/prospects/${p.id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="inline-flex text-gray-400 hover:text-[#1B365D]"
                                                                        title="Ouvrir dans un nouvel onglet"
                                                                    >
                                                                        <ExternalLink className="w-4 h-4" />
                                                                    </a>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Confirm dialog */}
                <Dialog open={!!confirmGroup} onOpenChange={(o) => !o && setConfirmGroup(null)}>
                    <DialogContent data-testid="doublon-confirm-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-[#1B365D]">
                                <GitMerge className="w-5 h-5 text-[#C9A962]" />
                                Confirmer la fusion
                            </DialogTitle>
                            <DialogDescription>
                                Cette action est irréversible. Les doublons sélectionnés seront déplacés dans la corbeille
                                et tous leurs événements / devis / inventaires / factures seront rattachés au prospect maître.
                            </DialogDescription>
                        </DialogHeader>
                        {confirmGroup && (() => {
                            const sel = selections[groupKey(confirmGroup)];
                            const master = confirmGroup.prospects.find(p => p.id === sel?.masterId);
                            const dups = confirmGroup.prospects.filter(p => sel?.dupIds?.has(p.id));
                            return (
                                <div className="space-y-3 text-sm">
                                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                        <p className="text-xs font-semibold text-blue-700 uppercase mb-1">Prospect maître (conservé)</p>
                                        <p className="font-medium text-[#1B365D]">{master?.nom}</p>
                                        <p className="text-gray-600 text-xs">{master?.email || master?.telephone}</p>
                                    </div>
                                    <div className="bg-red-50 border border-red-200 rounded p-3">
                                        <p className="text-xs font-semibold text-red-700 uppercase mb-1">
                                            {dups.length} doublon(s) fusionné(s) puis supprimé(s)
                                        </p>
                                        <ul className="space-y-1">
                                            {dups.map(d => (
                                                <li key={d.id} className="text-[#1B365D]">
                                                    • {d.nom} <span className="text-gray-500 text-xs">({d.email || d.telephone || '-'})</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            );
                        })()}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setConfirmGroup(null)} disabled={merging}>
                                Annuler
                            </Button>
                            <Button
                                onClick={doMerge}
                                disabled={merging}
                                className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white"
                                data-testid="doublon-confirm-merge-btn"
                            >
                                {merging ? 'Fusion...' : 'Confirmer la fusion'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
